import React, { createContext, useState, lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";
import { AuthProvider } from "./contexts_/AuthContext";
import { LiveDataProvider } from "./contexts_/LiveDataContext";
import { ChartViewProvider } from "./contexts_/ChartViewContext";
import { allRoutes } from "./seo/articleModel";
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Set axios defaults
axios.defaults.withCredentials = true;

// Create a QueryClient instance
const queryClient = new QueryClient();

// Create a RefreshContext to hold the refresh callback
export const RefreshContext = createContext();

// Lazy load route components
const Dashboard = lazy(() => import("./Dashboard"));
const PasswordReset = lazy(() => import("./pages_/PasswordReset"));
const TermsAndConditions = lazy(() => import("./pages_/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages_/PrivacyPolicy"));
const PlanPage = lazy(() => import("./components/PlanPage"));
const ArticlePage = lazy(() => import("./seo/ArticlePage"));



// Loading Screen Component
const LoadingScreen = () => {
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length < 3 ? prev + "." : ""));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Typography variant="h6" sx={{ marginBottom: 2 }}>
        Loading Spribe Results{dots}
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        <CircularProgress sx={{ color: "rgb(52, 180, 255)" }} />
        <CircularProgress sx={{ color: "rgb(145, 62, 248)" }} />
        <CircularProgress sx={{ color: "rgb(192, 23, 180)" }} />
      </Box>
    </Box>
  );
};

function AuthenticatedShell() {
  useEffect(() => {
    document.documentElement.lang = "en";
  }, []);
  return (
    <AuthProvider>
      <LiveDataProvider>
        <ChartViewProvider>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/terms&conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/plans" element={<PlanPage />} />
          </Routes>
        </ChartViewProvider>
      </LiveDataProvider>
    </AuthProvider>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {allRoutes().map((route) => (
          <Route key={route.path} path={route.path} element={<ArticlePage path={route.path} />} />
        ))}
        <Route path="/*" element={<AuthenticatedShell />} />
      </Routes>
    </Suspense>
  );
}

function AppContent() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

function App() {
  const [appKey, setAppKey] = useState(Date.now());

  const handleRefreshApp = () => {
    setAppKey(Date.now());
  };

  return (
    <QueryClientProvider client={queryClient}>
      <RefreshContext.Provider value={{ handleRefreshApp }}>
        <AppContent key={appKey} />
      </RefreshContext.Provider>
    </QueryClientProvider>
  );
}

export default App;
